<script setup lang="ts">
import { computed, inject, nextTick, onMounted, PropType, Ref, ref, watch } from 'vue';
import { RouteLocation, RouterLink, RouterView, useRouter } from 'vue-router';
import { EditQueueModal } from '@/components';
import {
  PopoverPositionEnum,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmSvg,
} from 'tir-components';
import { EllipsisHorizontalIcon, PlusIcon } from 'tir-style-system/icons/solid';
import { ChevronDownIcon, FolderIcon, ListBulletIcon } from 'tir-style-system/icons/outline';
import { GroupModel, NamespaceModel, QueueTasksModel } from '../models';
import { SidebarAddPopover, SidebarMenuItem, SidebarMorePopover } from '.';
import { TreeItemType } from '../enums';
import TirPmDraggable from '@/components/tir-pm-kanban/src/TirPmDraggable.vue';
import { DragModel } from '@/components/tir-pm-kanban';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { AdapterService } from '../adapter';
import { ProjectModel } from '@/models';

/**Маршруты */
const router = useRouter();

/**Параметры */
const props = defineProps({
  /** Элемент меню */
  menuItem: {
    type: Object as PropType<NamespaceModel | GroupModel | QueueTasksModel>,
    required: true,
  },
  /** Открыт ли sidebar */
  isSidebarOpen: {
    type: Boolean,
  },
  /** Уровень вложенности меню */
  level: {
    type: Number,
    default: 0,
  },
  /** Актуальная ширина sidebar */
  sidebarWidth: {
    type: Number,
    required: true,
  },
  /** Показывать только иконки */
  showOnlyIcons: {
    type: Boolean,
  },
  /**Статичное отображение сайдбара */
  isStatic: { type: Boolean, default: false },
  /**Есть элемент над которым выполняется какое либо действие */
  haveActionItem: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Событие при создании группы */
  (e: 'onCreateGroup', group: GroupModel): void;
  /**Событие при создании очереди задач */
  (e: 'onCreateQueue', queue: QueueTasksModel): void;
  /**Событие при удалении неймспейса */
  (e: 'onDeleteNamespace', namespaceId: string): void;
  /**Событие при удалении группы */
  (e: 'onDeleteGroup', groupId: number): void;
  /**Событие при удалении очереди */
  (e: 'onDeleteQueue', queueId: string): void;
  /**Событие при обновлении группы */
  (e: 'onUpdateGroup', group: GroupModel): void;
  /**Событие при обновлении очереди */
  (e: 'onUpdateQueue', queue: QueueTasksModel): void;
  /**Событие обновления неймспейса */
  (e: 'onUpdateNamespace', namespace: NamespaceModel): void;
  /**Перемещение группы/очереди */
  (e: 'onMove'): void;
  /**Редактирование очереди */
  (e: 'onEditQueue', queueId: string): void;
  /**Невозможно удалить очередь */
  (e: 'unableDeleteQueue', type: TreeItemType);
  /**Невозможно удалить группу */
  (e: 'unableDeleteGroup', type: TreeItemType);
  /**Невозможно удалить рабочую область */
  (e: 'unableDeleteNamespace', type: TreeItemType);
  /**Открытие рабочей области */
  (e: 'openNamespace', namespaceId: string, route: RouteLocation): void;
  /**Открытие папки */
  (e: 'openGroup', namespaceId: string, groupId: number, route: RouteLocation): void;
  /**Открытие очереди */
  (e: 'openQueue', namespaceId: string, queueId: string, route: RouteLocation): void;
  /**Выполняется действие над элементом */
  (e: 'update:haveActionItem', value: boolean): void;

  (e: 'getQueueItems', menuItem: GroupModel);
}>();

/**Текущий перетаскиваемый элемент */
const currentDragging = inject('currentDragging') as Ref<DragModel<GroupModel | QueueTasksModel>>;
/**Происходит ли перетаскивание */
const isDrag = inject('isDrag') as Ref<boolean>;
/**Директория над которой перетаскивается элемент */
const currentDragOver = inject('currentDragOver') as Ref<
  NamespaceModel | GroupModel | QueueTasksModel
>;

const adapter = new AdapterService();
/**Id редактируемой очереди */
const editQueue = ref<QueueTasksModel>();
/**Список id групп */
const tempGroupIds = [];
/**Открыт поповер добавления */
const isOpenedAddPopover = ref(false);
/**Открыт поповер доп действий */
const isOpenedMorePopover = ref(false);

/**Переключить флаг открытия поповера доп действий */
const toggleVisibleMorePopover = async (visible: boolean) => {
  isOpenedMorePopover.value = visible;
  if (visible) {
    setTimeout(() => {
      emit('update:haveActionItem', visible);
    });
  } else {
    emit('update:haveActionItem', visible);
  }
};
/**Переключить флаг открытия поповера добавления */
const toggleVisibleAddPopover = async (visible: boolean) => {
  isOpenedAddPopover.value = visible;
  if (visible) {
    setTimeout(() => {
      emit('update:haveActionItem', visible);
    });
  } else {
    emit('update:haveActionItem', visible);
  }
};
/**Состояние ховера */
const isHovered = ref(false);
/**Курсор наведен на container */
const isOver = ref(false);

/**Информация о проекте */
const project = inject('project') as Ref<ProjectModel>;

/**Объект для перетаскивания элемента */
const containerDragModel = computed(
  () =>
    new DragModel({
      Data: props.menuItem,
      AllowDrag: !props.menuItem.isNamespace() && !props.isStatic,
      AllowDrop: true,
    }),
);
/**Видно ли имя параметра */
const nameVisible = computed(() => (props.isSidebarOpen && !props.showOnlyIcons) || props.isStatic);
/**Классы контейнер элемента */
const containerClasses = computed(() => [
  'menu__container',
  { 'menu__container--close': !props.isSidebarOpen },
  { 'is-selected': props.menuItem.isSelectedInSidebar && !props.haveActionItem },
  { 'is-selected-secondary': props.menuItem.isSelectedInSidebar && props.haveActionItem },
  { 'is-active': isOpenedMorePopover.value || isOpenedAddPopover.value },
  { hidden: currentDragging.value?.Data?.Id == props.menuItem.Id },
  {
    over:
      isOver.value && isDrag.value && (props.menuItem.isGroup() || props.menuItem.isNamespace()),
  },
]);
/**Элемент рабочей области */
const namespaceItem = computed(() => (props.menuItem.isNamespace() ? props.menuItem : undefined));
/**Элемент группы */
const groupItem = computed(() => (props.menuItem.isGroup() ? props.menuItem : undefined));
/**Элемент очереди задач */
const queueItem = computed(() => (props.menuItem.isQueue() ? props.menuItem : undefined));

/**Дочерние группы */
const childrenGroups = computed(() => {
  if (namespaceItem.value?.Groups?.length) return namespaceItem.value.Groups;
  if (groupItem.value?.Groups?.length) return groupItem.value.Groups;
  return [];
});

/**Дочерние очереди */
const childrenQueues = computed(() => {
  if (namespaceItem.value?.QueueTasks?.length) return namespaceItem.value.QueueTasks;
  if (groupItem.value?.QueueTasks?.length) return groupItem.value.QueueTasks;
  return [];
});

/**Переход на страницу очереди */
const redirectQueuePage = computed(() => {
  return {
    name: 'queue',
    params: {
      projectId: project.value.Id,
      namespaceId: queueItem.value.NamespaceId,
      queueId: queueItem.value.Id,
    },
  };
});

/**Дочерние элементы */
const childrenItems = computed(() => [...childrenQueues.value, ...childrenGroups.value]);
/**ID очереди */
const namespaceId = computed(() => {
  if (namespaceItem.value) return namespaceItem.value.Id;
  if (groupItem.value) return groupItem.value.NamespaceId;
});
/** Открыто ли вложенное меню */
const isSubmenuOpen = computed({
  get() {
    if (namespaceItem.value) return namespaceItem.value.isOpenInSidebar;
    if (groupItem.value) return groupItem.value.isOpenInSidebar;
    return false;
  },
  set(value) {
    if (namespaceItem.value) return (namespaceItem.value.isOpenInSidebar = value);
    if (groupItem.value) return (groupItem.value.isOpenInSidebar = value);
  },
});
/** Вычисляемый отступ для элемента меню, если он вложен */
const translateXMenuWrapper = computed(() =>
  props.isSidebarOpen ? `translateX(${(props.level * 12) / 16 + 'rem'})` : 'translateX(0rem)',
);

/** Переклюатель видимости вложенного меню */
const toggleSubmenu = (visible?: any | boolean) => {
  isSubmenuOpen.value = typeof visible == 'boolean' ? visible : !isSubmenuOpen.value;
};
/** Ховер для элемента меню */
const toggleHoverMenuItem = (event: MouseEvent, addHover: boolean) => {
  if (!props.isSidebarOpen) return;
  isHovered.value = addHover;

  const target = event.target as HTMLElement | undefined;
  const menuBlock = target?.closest('.menu__container');

  if (!menuBlock) return;

  if (addHover) {
    menuBlock.classList.add('menu__container_hover');
  } else {
    menuBlock.classList.remove('menu__container_hover');
  }
};
const onClick = (event: any) => {
  if (
    event?.target?.closest('.menu__controls') ||
    event?.target?.closest('.sidebar-tree-item__chevron-button')
  )
    return;
  if (namespaceItem.value) {
    const route = router.resolve({
      name: 'namespace',
      params: { namespaceId: namespaceItem.value.Id },
    });
    emit('openNamespace', namespaceItem.value.Id, route);
    return;
  }
  if (groupItem.value) {
    const route = router.resolve({
      name: 'group',
      params: { groupId: groupItem.value.Id, namespaceId: groupItem.value.NamespaceId },
    });
    emit('openGroup', groupItem.value.NamespaceId, groupItem.value.Id, route);
    return;
  }
  if (queueItem.value) {
    localStorage.setItem('queueModel', JSON.stringify(queueItem.value));
    const route = router.resolve({
      name: 'queue',
      params: { queueId: queueItem.value.Id, namespaceId: queueItem.value.NamespaceId },
    });
    emit('openQueue', queueItem.value.NamespaceId, queueItem.value.Id, route);
    return;
  }
};
/**Обработка перетаскивания блока */
const onDrag = (_isDrag: boolean, _dragModel: DragModel<GroupModel | QueueTasksModel>) => {
  isDrag.value = _isDrag;
  if (_isDrag) {
    currentDragging.value = _dragModel;
  } else {
    setTimeout(() => {
      currentDragging.value = undefined;
      currentDragOver.value = undefined;
    });
  }
};
/**Переключение статуса наведения на container */
const onOver = (_isOver: boolean) => {
  if (!checkAllowOver()) {
    isOver.value = false;
    return;
  }

  isOver.value = _isOver;

  if (_isOver && !props.menuItem.isQueue()) {
    currentDragOver.value = props.menuItem;
    if (isDrag.value && !isSubmenuOpen.value && childrenItems.value.length) {
      toggleSubmenu();
    }
  }
};
/**Проверка разрешения наведения */
const checkAllowOver = () => {
  if (!currentDragging.value) return false;

  const item = currentDragging.value.Data;
  if (item.isGroup()) {
    const parentId = item.ParentGroupId || item.NamespaceId;
    const ids = [parentId, item.Id, ...getDeepArrayIds(item.Groups)];
    if (ids.includes(props.menuItem.Id)) return false;
  }

  return true;
};
/**Рекурсивное заполение id групп */
const getDeepArrayIds = (groups: GroupModel[]) => {
  groups?.forEach((item) => {
    if (item?.Id) {
      tempGroupIds.push(item.Id);
    }
    if (item?.Groups?.length) {
      getDeepArrayIds(item.Groups);
    }
  });
  return tempGroupIds;
};
/**Сброс элемента */
const onDrop = (_dragModel: DragModel<GroupModel>) => {
  if (
    props.menuItem.isQueue() ||
    !isDrag.value ||
    !currentDragging.value ||
    !currentDragOver.value ||
    !isOver.value
  ) {
    return;
  }

  emit('onMove');
};
/**
 * * Открытие окна для редактирования очереди
 */
const openEditQueueModal = () => {
  if (props.menuItem.isQueue()) {
    editQueue.value = props.menuItem;
  }
};

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (props.menuItem.QueueTasks?.find((x) => x.Id == router.currentRoute.value.params.queueId)) {
      isSubmenuOpen.value = props.menuItem.QueueTasks?.find(
        (x) => x.Id == router.currentRoute.value.params.queueId,
      );
    }
  },
  { immediate: true },
);

const visibleMoreOptions = computed(() => {
  const isOwner = CurrentUser.Roles.includes(UserRoleEnum.Owner);
  const isProjectAdmin = !!project.value.Admins?.some((a) => a.Id == CurrentUser.Id);

  return isOwner || isProjectAdmin || props.menuItem.IsAdminNamespace;
});
/**Вычисляет маршрут для <router-link> в зависимости от текущего элемента */
const computedRoute = computed(() => {
  if (namespaceItem.value) {
    const route = router.resolve({
      name: 'namespace',
      params: { namespaceId: namespaceItem.value.Id },
    });
    if (!props.isStatic) return route;
  }
  if (groupItem.value) {
    const route = router.resolve({
      name: 'group',
      params: { groupId: groupItem.value.Id, namespaceId: groupItem.value.NamespaceId },
    });
    if (!props.isStatic) return route;
  }
  if (queueItem.value) {
    const route = router.resolve({
      name: 'queue',
      params: { queueId: queueItem.value.Id, namespaceId: queueItem.value.NamespaceId },
    });
    if (!props.isStatic) return route;
  }
});

/**
 * * Закрыть окно редактирования очереди
 */
const closeEditQueueModal = () => (editQueue.value = undefined);
/**При создании папки */
const onCreateGroup = (group: GroupModel) => {
  emit('onCreateGroup', group);
  isSubmenuOpen.value = true;
};
/**При создании очереди */
const onCreateQueue = (task: QueueTasksModel) => {
  emit('onCreateQueue', task);
  isSubmenuOpen.value = true;
};
/**Отправка события для открытия рабочей области */
const openNamespace = (namespaceId: string, route: RouteLocation) => {
  emit('openNamespace', namespaceId, route);
};
/**Отправка события для открытия папки */
const openGroup = (namespaceId: string, groupId: number, route: RouteLocation) => {
  emit('openGroup', namespaceId, groupId, route);
};
/**Отправка события для открытия очереди */
const openQueue = (namespaceId: string, queueId: string, route: RouteLocation) => {
  emit('openQueue', namespaceId, queueId, route);
};
</script>
<template>
  <TirPmDraggable
    is-allow-click-delay
    :dragModel="containerDragModel"
    @dragging="onDrag"
    @over="onOver"
    @drop="onDrop"
    @dragstart.prevent
  >
    <RouterLink
      :class="containerClasses"
      :to="computedRoute"
      @mouseenter="toggleHoverMenuItem($event, true)"
      @mouseleave="toggleHoverMenuItem($event, false)"
      @click="onClick"
      @dblclick="toggleSubmenu"
    >
      <div
        class="menu__wrapper"
        :class="{ 'menu__wrapper_open-sidebar': isSidebarOpen }"
        :style="{
          transform: `${translateXMenuWrapper}`,
          width: isSidebarOpen ? `calc(100% - ${(level * 12) / 16 + 'rem'})` : '2rem',
        }"
      >
        <template v-if="namespaceItem">
          <div :class="['sidebar-menu-item__namespace-icon-wrap', { 'is-hover': !isSidebarOpen }]">
            <TirPmSvg
              :url="namespaceItem.IconUrl"
              class="sidebar-menu-item__namespace-icon"
              :color="namespaceItem.Color"
            />
          </div>
          <div class="menu__title_hidden" :class="{ menu__title_visible: nameVisible }">
            {{ namespaceItem.Name }}
          </div>
        </template>
        <template v-else-if="groupItem">
          <FolderIcon class="menu__icon" />
          <div class="menu__title_hidden" :class="{ menu__title_visible: nameVisible }">
            {{ groupItem.Name }}
          </div>
        </template>
        <template v-else-if="queueItem">
          <RouterLink :to="redirectQueuePage" class="menu__queue-item">
            <ListBulletIcon class="menu__icon" />
            <div class="menu__title_hidden" :class="{ menu__title_visible: nameVisible }">
              {{ queueItem.Name }}
            </div>
          </RouterLink>
        </template>

        <div
          class="menu__controls"
          v-if="
            isSidebarOpen &&
            !showOnlyIcons &&
            (isHovered || menuItem.isSelectedInSidebar || isOpenedAddPopover || isOpenedMorePopover)
          "
        >
          <template v-if="!isStatic && visibleMoreOptions">
            <TirPmButton
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Regular"
              :isPressed="isOpenedMorePopover"
              class="menu__button"
              @click.stop.prevent
            >
              <template #leftSlot>
                <SidebarMorePopover
                  :position="PopoverPositionEnum.Bottom"
                  :menu-item="menuItem"
                  @on-delete-namespace="emit('onDeleteNamespace', $event)"
                  @on-delete-group="emit('onDeleteGroup', $event)"
                  @on-delete-queue="emit('onDeleteQueue', $event)"
                  @unable-delete-queue="emit('unableDeleteQueue', $event)"
                  @unable-delete-group="emit('unableDeleteGroup', $event)"
                  @unable-delete-namespace="emit('unableDeleteNamespace', $event)"
                  @on-update-group="emit('onUpdateGroup', $event)"
                  @on-update-queue="emit('onUpdateQueue', $event)"
                  @on-update-namespace="emit('onUpdateNamespace', $event)"
                  @edit-queue="openEditQueueModal"
                  @toggle-visible="toggleVisibleMorePopover"
                >
                  <template #target>
                    <div class="sidebar-tree-item__dots-icon">
                      <EllipsisHorizontalIcon class="menu__controls-icon" />
                    </div>
                  </template>
                </SidebarMorePopover>
              </template>
            </TirPmButton>
            <TirPmButton
              v-if="menuItem.Type !== TreeItemType.Queue && namespaceId"
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Regular"
              :isPressed="isOpenedAddPopover"
              class="menu__button"
              @click.stop.prevent
            >
              <template #leftSlot>
                <SidebarAddPopover
                  :position="PopoverPositionEnum.Auto"
                  :namespace-id="namespaceId"
                  :parent-group-id="groupItem?.Id"
                  :location="menuItem.Type"
                  @on-create-group="onCreateGroup"
                  @on-create-queue="onCreateQueue"
                  @toggle-visible="toggleVisibleAddPopover"
                >
                  <template #target>
                    <div class="sidebar-tree-item__plus-icon">
                      <PlusIcon />
                    </div>
                  </template>
                </SidebarAddPopover>
              </template>
            </TirPmButton>
          </template>
        </div>
        <TirPmButton
          class="sidebar-tree-item__chevron-button"
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
          v-if="isSidebarOpen && childrenItems.length"
          @click.stop.prevent
        >
          <template #leftSlot>
            <div class="sidebar-tree-item__chevron-icon">
              <ChevronDownIcon
                class="menu__controls-icon"
                :class="{ 'menu__controls-icon_submenu-open': isSubmenuOpen }"
                @click="toggleSubmenu"
              />
            </div>
          </template>
        </TirPmButton>
      </div>
    </RouterLink>
  </TirPmDraggable>
  <div v-if="childrenItems.length && isSubmenuOpen" class="menu__submenu">
    <div
      :class="[
        'menu__submenu-wrapper-' + props.level,
        { 'menu__submenu-wrapper_shown': isSubmenuOpen },
      ]"
      class="menu__submenu-wrapper"
    >
      <SidebarMenuItem
        v-for="(item, idx) in childrenItems"
        :key="idx"
        :menu-item="item"
        :is-sidebar-open="isSidebarOpen"
        :level="+props.level + 1"
        :sidebarWidth="sidebarWidth"
        :showOnlyIcons="showOnlyIcons"
        :isStatic="isStatic"
        :have-action-item="haveActionItem"
        @on-delete-namespace="emit('onDeleteNamespace', $event)"
        @on-delete-group="emit('onDeleteGroup', $event)"
        @on-delete-queue="emit('onDeleteQueue', $event)"
        @on-create-group="emit('onCreateGroup', $event)"
        @on-create-queue="emit('onCreateQueue', $event)"
        @on-update-group="emit('onUpdateGroup', $event)"
        @on-update-queue="emit('onUpdateQueue', $event)"
        @on-update-namespace="emit('onUpdateNamespace', $event)"
        @on-move="emit('onMove')"
        @unable-delete-queue="emit('unableDeleteQueue', $event)"
        @unable-delete-group="emit('unableDeleteGroup', $event)"
        @unable-delete-namespace="emit('unableDeleteNamespace', $event)"
        @open-namespace="openNamespace"
        @open-group="openGroup"
        @open-queue="openQueue"
        @update:have-action-item="emit('update:haveActionItem', $event)"
      />
    </div>
  </div>
  <EditQueueModal :queue="editQueue" @close="closeEditQueueModal" />
</template>

<style scoped lang="scss">
.menu__controls-chevron-down {
  display: flex;
  align-items: center;
}
.sidebar-menu-item {
  &__namespace-icon {
    font-size: 1.5rem;
    --tir-pm-svg__bg-layer-color: transparent;
  }
  &__namespace-icon-wrap {
    align-self: flex-start;
    padding: 0.25rem;

    transition: padding 0.2s;
  }

  &__namespace-icon-wrap.is-hover:hover {
    background: var(--neutral-translucent-5, rgba(39, 45, 55, 0.05));
    border-radius: 1rem;
  }
}
.menu__queue-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text-primary, #272d37);
  width: 100%;
  min-width: 1rem;
}

.sidebar-tree-item {
  &__plus-icon {
    width: 1rem;
    height: 1rem;
  }
  &__chevron-icon {
    width: 1rem;
    height: 1rem;
  }
}

.menu__container {
  border-radius: 0.375rem;
  cursor: pointer;
  position: relative;
  display: flex;
  padding: 0.125rem 0;
  &--close {
    width: max-content;
  }
  .menu__wrapper_open-sidebar {
    .sidebar-menu-item {
      &__namespace-icon-wrap {
        padding: 0.25rem 0.375rem 0.25rem 0.75rem;
      }
    }
    .menu__icon {
      width: 3rem;
      padding: 0 0.375rem 0 1rem;
    }
  }
  &.is-selected-secondary {
    background-color: var(--neutral-translucent-5, rgba(39, 45, 55, 0.05));
  }

  &.is-active:not(.hidden),
  &.is-selected:not(.hidden),
  &:active:not(.hidden) {
    background: var(--neutral-translucent-10, #272d371a);
    &:hover {
      background: var(--neutral-translucent-20, #272d3733);
    }
  }
}

.menu__button {
  width: 0;
  height: 100%;
}

.menu__container_hover {
  background: var(--neutral-translucent-5, #272d370d);
  transition: background-color 0.3s;
}

.menu__wrapper {
  width: 2.625rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 2rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: var(--text-primary, #272d37);
  position: relative;
  transition: transform 0.3s;
}

.menu__wrapper_open-sidebar {
  padding-right: 0.5rem;
  transition: transform 0.3s;
}

.menu__icon {
  padding: 0 0.5rem;
  width: 2rem;
  height: 1rem;
  flex-shrink: 0;
}

.menu-custom__icon {
  width: 0.938rem;
  height: 0.938rem;
  padding: 0.25rem;
  border-radius: 0.5rem;
  box-sizing: content-box;
  margin: 0 auto;

  &.marketing {
    background-color: var(--bg-info);
    color: #3a7cc2;
  }
  &.management {
    background-color: var(--bg-attention);
    color: var(--text-attention);
  }
  &.product {
    background-color: var(--bg-negative);
    color: #ff5249;
  }
}

.menu__title_hidden {
  width: 0;
  opacity: 0;
  // min-height: 1.5rem;
  line-height: 1.5rem;
  transform: translateX(-10%);
  transition: all 0s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu__title_visible {
  width: 100%;
  opacity: 1;
  transform: translateX(0);
  transition: all 10s, width 0.3s ease, opacity 0.2s ease 0.3s, transform 0.2s ease 0.3s,
    height 0s ease 0.3s;
}

.menu__controls {
  display: flex;
  align-items: center;
}

.menu__controls-icon {
  width: 1rem;
  height: 1rem;
  rotate: 0deg;
  transition: 0.3s rotate;
}

.menu__controls-icon_submenu-open {
  rotate: 180deg;
  transition: 0.3s rotate;
}

.menu__submenu-wrapper {
  transform: translateY(-30%);
  transition: 0.3s transform;
}

.menu__submenu-wrapper_shown {
  transform: translateY(0rem);
}

.sidebar-tree-item__dots-icon {
  display: flex;
}
</style>
<style lang="scss">
.menu__container {
  &::before {
    content: '';
    position: absolute;
    left: -0.125rem;
    top: -0.125rem;
    width: 100%;
    height: 100%;
    border: 0.125rem solid transparent;
    border-radius: 0.5rem;
    transition: all 0.1s ease;
  }
  &.dragging {
    z-index: 10000;
    box-shadow: 0rem 0rem 0.5rem 0.25rem rgba(0, 0, 0, 0.039);
    background-color: #fff !important;
    opacity: 0.7;
    .menu__controls {
      display: none;
    }
  }
  &.hidden {
    animation: blink 1s infinite;
    transition: background-color 1s ease !important;
    @keyframes blink {
      0% {
        background-color: #fff;
      }
      50% {
        background-color: rgba(39, 45, 55, 0.05);
      }
    }

    & > * {
      opacity: 0;
    }
  }
  &:not(.hidden).over:before {
    border-color: #2a77ef;
  }
}

.sidebar-tree-item__chevron-button {
  width: 1.5rem !important;
  height: 1.5rem !important;
}
</style>
